import { useState, useMemo } from 'react'
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search, ChevronsLeft, ChevronsRight } from 'lucide-react'

export interface Column<T> {
  key: keyof T | string
  header: React.ReactNode
  sortable?: boolean
  render?: (item: T) => React.ReactNode
  className?: string
  headerClassName?: string
}

interface DataTableProps<T> {
  data: T[]
  columns: Column<T>[]
  searchPlaceholder?: string
  searchKeys?: (keyof T)[]
  pageSize?: number
  emptyMessage?: string
  onRowClick?: (item: T) => void
  rowClassName?: string | ((item: T) => string)
  getRowKey: (item: T) => string | number
  defaultSortKey?: string
  defaultSortDirection?: SortDirection
  controlledSort?: { key: string | null; direction: SortDirection }
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void
}

type SortDirection = 'asc' | 'desc' | null

export default function DataTable<T extends Record<string, any>>({
  data,
  columns,
  searchPlaceholder = 'Search...',
  searchKeys = [],
  pageSize = 10,
  emptyMessage = 'No records found',
  onRowClick,
  rowClassName,
  getRowKey,
  defaultSortKey,
  defaultSortDirection,
  controlledSort,
  onSortChange
}: DataTableProps<T>) {
  const [search, setSearch] = useState('')
  const [internalSortKey, setInternalSortKey] = useState<string | null>(defaultSortKey ?? null)
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>(defaultSortDirection ?? null)
  const [currentPage, setCurrentPage] = useState(1)
  const isControlled = !!controlledSort
  const sortKey = isControlled ? controlledSort!.key : internalSortKey
  const sortDirection = isControlled ? controlledSort!.direction : internalSortDirection

  const filteredData = useMemo(() => {
    if (!search.trim() || searchKeys.length === 0) return data
    
    const searchLower = search.toLowerCase()
    return data.filter(item => 
      searchKeys.some(key => {
        const value = item[key]
        if (value == null) return false
        return String(value).toLowerCase().includes(searchLower)
      })
    )
  }, [data, search, searchKeys])

  const sortedData = useMemo(() => {
    if (isControlled) return filteredData
    if (!sortKey || !sortDirection) return filteredData
    
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortKey]
      const bVal = b[sortKey]
      
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      
      if (aVal instanceof Date && bVal instanceof Date) {
        return sortDirection === 'asc' ? aVal.getTime() - bVal.getTime() : bVal.getTime() - aVal.getTime()
      }
      
      const aStr = String(aVal).toLowerCase()
      const bStr = String(bVal).toLowerCase()
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
    })
  }, [filteredData, sortKey, sortDirection])

  const totalPages = Math.ceil(sortedData.length / pageSize)
  
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [sortedData, currentPage, pageSize])

  const handleSort = (key: string) => {
    if (isControlled && onSortChange) {
      const nextDir: 'asc' | 'desc' = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc'
      onSortChange(key, nextDir)
      return
    }
    if (sortKey === key) {
      if (sortDirection === 'asc') {
        setInternalSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setInternalSortKey(null)
        setInternalSortDirection(null)
      }
    } else {
      setInternalSortKey(key)
      setInternalSortDirection('asc')
    }
    setCurrentPage(1)
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  const getNestedValue = (obj: T, key: string): any => {
    return key.split('.').reduce((acc: any, part) => acc?.[part], obj)
  }

  return (
    <div className="space-y-4">
      {searchKeys.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-chestnut-charcoal/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-10 pr-4 py-2 border border-chestnut-charcoal/20 rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 focus:border-chestnut-forest"
          />
        </div>
      )}

      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-chestnut-charcoal/10">
                {columns.map((column) => (
                  <th
                    key={String(column.key)}
                    className={`px-4 py-3 text-left text-sm font-semibold text-chestnut-charcoal ${column.headerClassName || ''} ${column.sortable ? 'cursor-pointer select-none hover:bg-chestnut-cream/50' : ''}`}
                    onClick={() => column.sortable && handleSort(String(column.key))}
                  >
                    <div className="flex items-center gap-1">
                      {column.header}
                      {column.sortable && (
                        <span className="flex flex-col">
                          <ChevronUp className={`h-3 w-3 -mb-1 ${sortKey === column.key && sortDirection === 'asc' ? 'text-chestnut-forest' : 'text-chestnut-charcoal/30'}`} />
                          <ChevronDown className={`h-3 w-3 ${sortKey === column.key && sortDirection === 'desc' ? 'text-chestnut-forest' : 'text-chestnut-charcoal/30'}`} />
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-chestnut-charcoal/10">
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-chestnut-charcoal/60">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                paginatedData.map((item) => {
                  const rowClass = typeof rowClassName === 'function' ? rowClassName(item) : rowClassName
                  return (
                    <tr
                      key={getRowKey(item)}
                      className={`hover:bg-chestnut-cream/30 transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${rowClass || ''}`}
                      onClick={() => onRowClick?.(item)}
                    >
                      {columns.map((column) => (
                        <td key={String(column.key)} className={`px-4 py-3 text-sm ${column.className || ''}`}>
                          {column.render 
                            ? column.render(item) 
                            : getNestedValue(item, String(column.key)) ?? '-'
                          }
                        </td>
                      ))}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-chestnut-charcoal/10">
          <div className="text-sm text-chestnut-charcoal/60">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} results
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg hover:bg-chestnut-cream disabled:opacity-30 disabled:cursor-not-allowed"
              title="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg hover:bg-chestnut-cream disabled:opacity-30 disabled:cursor-not-allowed"
              title="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-1 px-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-chestnut-forest text-white'
                        : 'hover:bg-chestnut-cream text-chestnut-charcoal'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg hover:bg-chestnut-cream disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg hover:bg-chestnut-cream disabled:opacity-30 disabled:cursor-not-allowed"
              title="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
